import React, { useEffect, useRef, useState } from "react";
import { useBranch } from "@/context/BranchContext";
import { useLanguage } from "@/context/LanguageContext";
import { useAuth } from "@/context/AuthContext";
import { Building2, Check, ChevronDown, Star } from "lucide-react";
import { Link } from "react-router-dom";

export default function BranchSwitcher() {
  const { branches, activeBranchId, activeBranch, switchBranch } = useBranch();
  const { lang } = useLanguage();
  const { canAccess, isAdmin } = useAuth();
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const onDoc = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  if (!branches || branches.length === 0) return null;
  // Hide entirely when user has access to a single branch — no need to switch
  if (branches.length === 1) return null;
  const canManage = isAdmin || canAccess("branches");
  const current = activeBranch || branches.find((b) => b.is_default) || branches[0];
  const label = lang === "ar" ? (current?.name_ar || current?.name_en) : (current?.name_en || current?.name_ar);

  return (
    <div ref={ref} className="relative" data-testid="branch-switcher">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="flex items-center gap-2 px-3 py-1.5 rounded-md border border-white/30 hover:border-white bg-white/15 text-sm text-white transition"
        data-testid="branch-switcher-btn"
      >
        <Building2 size={14} className="text-white" />
        <span className="max-w-[160px] truncate">{label || (lang === "ar" ? "الفرع الرئيسي" : "Main Branch")}</span>
        <ChevronDown size={12} className="text-white/70" />
      </button>

      {open && (
        <div
          className="absolute end-0 top-full mt-1 w-72 rounded-md border border-[var(--border)] bg-[var(--surface)] shadow-xl z-50 overflow-hidden"
          data-testid="branch-switcher-menu"
        >
          <div className="px-3 py-2 border-b border-[var(--border-subtle)] text-[10px] font-semibold uppercase tracking-wider text-[var(--text-muted)]">
            {lang === "ar" ? "الفروع" : "Branches"}
          </div>
          <div className="max-h-72 overflow-auto">
            {branches.map((b) => {
              const active = b.branch_id === activeBranchId || (!activeBranchId && b.is_default);
              const name = lang === "ar" ? (b.name_ar || b.name_en) : (b.name_en || b.name_ar);
              return (
                <button
                  key={b.branch_id}
                  type="button"
                  onClick={() => switchBranch(b.branch_id)}
                  className={`w-full text-start px-3 py-2.5 flex items-center justify-between gap-2 text-sm transition ${
                    active ? "bg-[var(--gold-tint)] text-[var(--gold)]" : "text-[var(--text)] hover:bg-[var(--surface-2)]"
                  }`}
                  data-testid={`branch-opt-${b.branch_id}`}
                >
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-1.5 truncate">
                      <span className="truncate">{name}</span>
                      {b.is_default && <Star size={10} className="text-[var(--gold)] fill-[var(--gold)] shrink-0" />}
                    </div>
                    {b.address && <div className="text-[11px] text-[var(--text-muted)] truncate">{b.address}</div>}
                  </div>
                  {active && <Check size={14} className="shrink-0" />}
                </button>
              );
            })}
          </div>
          {canManage && (
          <Link
            to="/branches"
            className="block px-3 py-2 border-t border-[var(--border-subtle)] text-xs text-[var(--gold)] hover:bg-[var(--surface-2)] transition"
            onClick={() => setOpen(false)}
            data-testid="branch-switcher-manage"
          >
            {lang === "ar" ? "إدارة الفروع ←" : "Manage branches →"}
          </Link>
          )}
        </div>
      )}
    </div>
  );
}
